import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import * as actionTypes from "./actionTypes";
import {
  createModuleExclusionAPI,
  deleteModuleExclusionAPI,
  duplicateModuleExclusionAPI,
  getModulesAPI,
  getModulesExclusionsAPI,
  getModulesMenusAPI,
  getModulesTreeAPI,
  updateModuleAPI,
  updateModuleExclusionAPI,
  updateModuleMenuAPI,
} from "@icarius-connection/api";
import { getLocalizedString, getErrorStringFromServerError } from "@icarius-localization/strings";

const errorHandler = (e, dispatch) => {
  let errorString = "";
  switch (e.response?.data?.status) {
    case "INVALID_DATA":
    case "VALIDATION_ERROR":
      errorString = e.response?.data?.error === "INVALID_EXTENSION" ? getLocalizedString("invalidFileExtension") : getLocalizedString("validationError");
      break;
    default:
      errorString = getErrorStringFromServerError(e);
      break;
  }
  dispatch(openSnackbarAction({ msg: errorString, severity: "error" }));
}

// modules
export const getModulesAction = () => async (dispatch) => {
  dispatch({ type: actionTypes.GET_MODULES });
  try {
    const response = await getModulesAPI();

    const modules = response.data?.modules;
    const collaboratorsList = response.data?.collaboratorsList;
    const resources = response.data?.resources;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_MODULES_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_MODULES_FULFILLED,
      payload: {
        modules,
        collaboratorsList,
        resources,
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_MODULES_REJECTED, payload: e });
    errorHandler(e, dispatch, "module");
    return e.response;
  }
};

export const updateModuleAction = (moduleId, dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.UPDATE_MODULE });
  try {
    const response = await updateModuleAPI(moduleId, dataToSend);

    const modules = response.data?.modules;
    const collaboratorsList = response.data?.collaboratorsList;
    const resources = response.data?.resources;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.UPDATE_MODULE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.UPDATE_MODULE_FULFILLED,
      payload: {
        modules,
        collaboratorsList,
        resources,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("moduleUpdatedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.UPDATE_MODULE_REJECTED, payload: e });
    errorHandler(e, dispatch);
    return e.response;
  }
};

// menus
export const getModulesMenusAction = () => async (dispatch) => {
  dispatch({ type: actionTypes.GET_MENUS });
  try {
    const response = await getModulesMenusAPI();

    const menus = response.data?.menus;
    const resources = response.data?.resources;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_MENUS_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_MENUS_FULFILLED,
      payload: {
        menus,
        resources,
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_MENUS_REJECTED, payload: e });
    errorHandler(e, dispatch);
    return e.response;
  }
};

export const updateModuleMenuAction = (menuId, dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.UPDATE_MENU });
  try {
    const response = await updateModuleMenuAPI(menuId, dataToSend);

    const menus = response.data?.menus;
    const resources = response.data?.resources;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.UPDATE_MENU_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.UPDATE_MENU_FULFILLED,
      payload: {
        menus,
        resources,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("menuUpdatedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.UPDATE_MENU_REJECTED, payload: e });
    errorHandler(e, dispatch);
    return e.response;
  }
};

// exclusions
export const getModulesExclusionsAction = () => async (dispatch) => {
  dispatch({ type: actionTypes.GET_EXCLUSIONS });
  try {
    const response = await getModulesExclusionsAPI();

    const exclusions = response.data?.exclusions;
    const moduleList = response.data?.moduleList;
    const menuList = response.data?.menuList;
    const collaboratorsList = response.data?.collaboratorsList;
    const resources = response.data?.resources;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_EXCLUSIONS_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_EXCLUSIONS_FULFILLED,
      payload: {
        exclusions,
        moduleList,
        menuList,
        collaboratorsList,
        resources,
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_EXCLUSIONS_REJECTED, payload: e });
    errorHandler(e, dispatch);
    return e.response;
  }
};

export const createModuleExclusionAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.CREATE_EXCLUSIONS });
  try {
    const response = await createModuleExclusionAPI(dataToSend);

    const exclusions = response.data?.exclusions;
    const moduleList = response.data?.moduleList;
    const menuList = response.data?.menuList;
    const collaboratorsList = response.data?.collaboratorsList;
    const resources = response.data?.resources;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.CREATE_EXCLUSIONS_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.CREATE_EXCLUSIONS_FULFILLED,
      payload: {
        exclusions,
        moduleList,
        menuList,
        collaboratorsList,
        resources,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("exclusionCreatedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.CREATE_EXCLUSIONS_REJECTED, payload: e });
    errorHandler(e, dispatch);
    return e.response;
  }
};

export const updateModuleExclusionAction = (exclusionId, dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.UPDATE_EXCLUSIONS });
  try {
    const response = await updateModuleExclusionAPI(exclusionId, dataToSend);

    const exclusions = response.data?.exclusions;
    const moduleList = response.data?.moduleList;
    const menuList = response.data?.menuList;
    const collaboratorsList = response.data?.collaboratorsList;
    const resources = response.data?.resources;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.UPDATE_EXCLUSIONS_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.UPDATE_EXCLUSIONS_FULFILLED,
      payload: {
        exclusions,
        moduleList,
        menuList,
        collaboratorsList,
        resources,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("exclusionUpdatedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.UPDATE_EXCLUSIONS_REJECTED, payload: e });
    errorHandler(e, dispatch);
    return e.response;
  }
};

export const deleteModuleExclusionAction = (exclusionId) => async (dispatch) => {
  dispatch({ type: actionTypes.DELETE_EXCLUSIONS });
  try {
    const response = await deleteModuleExclusionAPI(exclusionId);

    const exclusions = response.data?.exclusions;
    const moduleList = response.data?.moduleList;
    const menuList = response.data?.menuList;
    const collaboratorsList = response.data?.collaboratorsList;
    const resources = response.data?.resources;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.DELETE_EXCLUSIONS_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.DELETE_EXCLUSIONS_FULFILLED,
      payload: {
        exclusions,
        moduleList,
        menuList,
        collaboratorsList,
        resources,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("exclusionDeletedOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.DELETE_EXCLUSIONS_REJECTED, payload: e });
    errorHandler(e, dispatch);
    return e.response;
  }
};

export const duplicateModuleExclusionAction = (exclusionId, dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.DUPLICATE_EXCLUSIONS });
  try {
    const response = await duplicateModuleExclusionAPI(exclusionId, dataToSend);

    const exclusions = response.data?.exclusions;
    const moduleList = response.data?.moduleList;
    const menuList = response.data?.menuList;
    const collaboratorsList = response.data?.collaboratorsList;
    const resources = response.data?.resources;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.DUPLICATE_EXCLUSIONS_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.DUPLICATE_EXCLUSIONS_FULFILLED,
      payload: {
        exclusions,
        moduleList,
        menuList,
        collaboratorsList,
        resources,
      },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("exclusionDuplicateOk"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.DUPLICATE_EXCLUSIONS_REJECTED, payload: e });
    errorHandler(e, dispatch);
    return e.response;
  }
};

// tree
export const getModulesTreeAction = (role, codemp) => async (dispatch) => {
  dispatch({ type: actionTypes.GET_TREE });
  try {
    const response = await getModulesTreeAPI(`?role=${role}&codemp=${codemp}`);

    const tree = response.data?.tree;
    const status = response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_TREE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_TREE_FULFILLED,
      payload: {
        tree,
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_TREE_REJECTED, payload: e });
    errorHandler(e, dispatch);
    return e.response;
  }
};